/**
 * Contact Address Display Component
 *
 * Resolves and displays contact address with country/state/city names
 * based on the contact's addressLang setting.
 *
 * Each contact can have a different addressLang, so this component
 * loads the appropriate location data for each contact individually.
 */

import { useMemo } from 'react';
import { useCascadingLocations, useCountriesData } from '@/hooks/useLocations';
import { formatAddressWithResolvedNames } from '@/utils/locationUtils';
import { AddressParseFailedTag } from '@/components/common/AddressParseFailedTag';
import type { AddressParseStatus } from '@/hooks/mockParseAddress';

export interface ContactAddressData {
  countryId?: number | null;
  stateId?: number | null;
  cityId?: number | null;
  address?: string | null;
  postcode?: string | null;
  addressLang?: string | null;
  /** board2-#79/#101: backend address-parse status — 'failed' renders a warning Tag next to the raw address */
  parseStatus?: AddressParseStatus | null;
  // Also accept pre-resolved strings from API (optional, for list endpoint)
  country?: string | null;
  state?: string | null;
  city?: string | null;
}

interface ContactAddressDisplayProps {
  contact: ContactAddressData;
  fallback?: string;
}

/**
 * Displays a contact's address with resolved location names.
 * Loads location data based on the contact's addressLang.
 *
 * IMPORTANT: When API returns pre-resolved city/state/country strings (from list API),
 * use those directly. When only IDs are provided (from detail API), resolve via hooks.
 * When IDs are 0 or missing, just show the address field directly.
 */
export function ContactAddressDisplay({ contact, fallback = '-' }: ContactAddressDisplayProps) {
  // Check if API already provides pre-resolved location names (from list API)
  const hasPreResolvedNames = !!(contact.city || contact.state || contact.country);

  // Check if contact has valid location IDs (0 or missing means no location data)
  const hasValidLocationIds = !!(
    (contact.countryId && contact.countryId > 0) ||
    (contact.stateId && contact.stateId > 0) ||
    (contact.cityId && contact.cityId > 0)
  );

  // Always call hooks (React rules) but only use them when needed
  // Load countries with contact's addressLang for correct localized names
  const { countries } = useCountriesData(contact.addressLang ?? undefined);

  // Resolve countryCode from countryId for cascading location fetch
  const countryCode = useMemo(() => {
    if (hasPreResolvedNames || !hasValidLocationIds || !contact.countryId || !countries) return null;
    const country = countries.find((c) => c.countryId === contact.countryId);
    return country?.countryCode || null;
  }, [hasPreResolvedNames, hasValidLocationIds, contact.countryId, countries]);

  // Load states and cities based on contact's location IDs and addressLang
  const { states, cities } = useCascadingLocations(
    countryCode,
    contact.stateId ?? null,
    contact.addressLang ?? undefined // Use contact's addressLang for name resolution
  );

  // Format the address with resolved names
  const displayAddress = useMemo(() => {
    // If API provides pre-resolved names, use them directly
    if (hasPreResolvedNames) {
      const parts: string[] = [];
      if (contact.address) parts.push(contact.address);
      if (contact.city) parts.push(contact.city);
      if (contact.state) parts.push(contact.state);
      if (contact.postcode) parts.push(contact.postcode);
      if (contact.country) parts.push(contact.country);
      return parts.length > 0 ? parts.join(', ') : fallback;
    }

    // No valid location IDs - just show address directly
    if (!hasValidLocationIds) {
      return contact.address || fallback;
    }

    // Resolve names from IDs (for detail API or manual data)
    return formatAddressWithResolvedNames(
      {
        address: contact.address,
        cityId: contact.cityId,
        stateId: contact.stateId,
        postcode: contact.postcode,
        countryId: contact.countryId,
      },
      countries || [],
      states || [],
      cities || [],
      fallback
    );
  }, [hasPreResolvedNames, hasValidLocationIds, contact, countries, states, cities, fallback]);

  // #101 BE-03: an unparseable row shows the raw address + a visible warning
  // Tag instead of a silent pending+dash ('pending' stays tag-less on purpose)
  if (contact.parseStatus === 'failed') {
    return (
      <>
        {displayAddress}
        <AddressParseFailedTag />
      </>
    );
  }

  return <>{displayAddress}</>;
}

export default ContactAddressDisplay;
